import { useLocalStorage } from "@/hooks/useLocalStorage";
import {
  HABITS_BY_ELEMENT, HABITS_HOW_TO, HABITS_SIDE_CARDS,
  MORNING_ROUTINE, MORNING_EXTRAS,
  EVENING_ROUTINE, EVENING_EXTRAS,
  CHECKLIST, CHECKLIST_ESCALAS, CHECKLIST_TEXTOS, CHECKLIST_NOTE,
} from "@/data/practices";
import { elementColor } from "@/data/theme";
import { PageHeader, SafetyNote, SourceTag } from "@/components/common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Sunrise, Moon, RotateCcw, AlertCircle, Heart, Trees, CheckSquare, Eye, Edit3, RefreshCw } from "lucide-react";

const HABIT_HOW_TO_ICONS = [Trees, CheckSquare, Eye, Edit3, RefreshCw];

// Data civil local no navegador (substitui UTC para evitar virada às 21h em Brasília)
const todayKey = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function ChecklistColumn({ title, items, state, onToggle, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
      <h4 className="text-xs uppercase tracking-widest text-gold">{title}</h4>
      <div className="mt-3 space-y-1">
        {items.map((item) => {
          const checked = !!state[item];
          return (
            <label key={item} data-testid={`check-${item}`} className="flex cursor-pointer items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-secondary">
              <Checkbox checked={checked} onCheckedChange={() => onToggle(item)} />
              <span className={`text-sm ${checked ? "text-muted-foreground line-through" : "text-foreground"}`}>{item}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function SelfCare() {
  const [data, setData] = useLocalStorage(`cc-checklist-${todayKey()}`, {
    items: {},
    escalas: {},
    textos: {},
    nota: "",
  });
  const allItems = [...CHECKLIST.manha, ...CHECKLIST.dia, ...CHECKLIST.noite];
  const done = allItems.filter((i) => data.items?.[i]).length;
  const pct = Math.round((done / allItems.length) * 100);

  const toggle = (item) => setData((d) => ({
    ...d,
    items: { ...(d.items || {}), [item]: !d.items?.[item] },
  }));

  const setEscala = (k, v) => setData((d) => ({
    ...d,
    escalas: { ...(d.escalas || {}), [k]: v },
  }));

  const setTexto = (k, v) => setData((d) => ({
    ...d,
    textos: { ...(d.textos || {}), [k]: v },
  }));

  const reset = () => setData({ items: {}, escalas: {}, textos: {}, nota: "" });

  return (
    <div className="space-y-10">
      <PageHeader
        eyebrow="Hábitos e Autocuidado"
        title="Pequenas escolhas, feitas todos os dias"
        subtitle="Práticas simples de observação e autocuidado inspiradas na MTC. Comece pequeno: escolha uma ação por dia e repita com gentileza. Autocuidado não deve virar cobrança."
      />

      <Tabs defaultValue="habitos" className="w-full">
        <TabsList className="flex flex-wrap h-auto gap-2 bg-secondary/60 p-1.5">
          <TabsTrigger value="habitos" data-testid="tab-habitos" className="rounded-full">Hábitos por elemento</TabsTrigger>
          <TabsTrigger value="rotinas" data-testid="tab-rotinas" className="rounded-full">Rotinas manhã & noite</TabsTrigger>
          <TabsTrigger value="checklist" data-testid="tab-checklist" className="rounded-full">Checklist diário</TabsTrigger>
        </TabsList>

        {/* HÁBITOS */}
        <TabsContent value="habitos" className="mt-6 space-y-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-serif text-xl font-medium">Hábitos simples para cada elemento</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Pequenas ações para cultivar equilíbrio no dia a dia.</p>
            </div>
            <SourceTag source={{ pdfPage: 42, printedPage: "40" }} />
          </div>

          <div className="grid gap-6 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="grid gap-5 sm:grid-cols-2">
                {HABITS_BY_ELEMENT.map((h) => {
                  const c = elementColor(h.elemento);
                  return (
                    <div key={h.id} data-testid={`habit-card-${h.id}`} className="rounded-3xl border border-border p-6" style={{ background: `linear-gradient(160deg, ${c.soft}, hsl(var(--card)))` }}>
                      <div className="flex items-center justify-between">
                        <h3 className="font-serif text-xl font-semibold" style={{ color: c.text }}>{c.label}</h3>
                        <SourceTag source={h.source} />
                      </div>
                      <p className="text-sm font-medium" style={{ color: c.primary }}>{h.qualidade}</p>
                      <ul className="mt-3 space-y-1.5">
                        {h.habitos.map((item, i) => (
                          <li key={i} className="flex gap-2 text-sm" style={{ color: c.text }}><span>•</span> {item}</li>
                        ))}
                      </ul>
                      <p className="mt-3 text-xs" style={{ color: c.primary }}>{h.orgaos}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Cartões laterais do PDF: Comece pequeno & Sem excesso */}
            <div className="lg:col-span-4 space-y-5">
              <div className="rounded-3xl border border-border bg-card p-6">
                <h4 className="font-serif text-lg font-semibold text-foreground">{HABITS_SIDE_CARDS.comecePequeno.titulo}</h4>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  {HABITS_SIDE_CARDS.comecePequeno.texto}
                </p>
              </div>

              <div className="rounded-3xl border border-border bg-card p-6">
                <h4 className="font-serif text-lg font-semibold text-foreground">{HABITS_SIDE_CARDS.semExcesso.titulo}</h4>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  {HABITS_SIDE_CARDS.semExcesso.texto}
                </p>
              </div>
            </div>
          </div>

          {/* Como praticar */}
          <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
            <h4 className="font-serif text-xl font-medium text-foreground text-center sm:text-left">Como praticar</h4>
            <div className="mt-6 grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {HABITS_HOW_TO.map((passo, i) => {
                const Icon = HABIT_HOW_TO_ICONS[i] || Trees;
                return (
                  <div key={i} className="flex flex-col items-center sm:items-start text-center sm:text-left rounded-2xl bg-secondary/40 p-4">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gold/20 text-gold text-xs font-semibold mb-2">
                      <Icon className="h-4 w-4" />
                    </span>
                    <p className="text-xs text-muted-foreground">{passo}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-secondary/50 p-6 text-center">
            <p className="text-sm italic text-muted-foreground leading-relaxed">
              {HABITS_SIDE_CARDS.frase}
            </p>
          </div>

          <SafetyNote>Use como referência educativa, sem transformar sugestões em regras rígidas. Observe seu corpo com gentileza.</SafetyNote>
        </TabsContent>

        {/* ROTINAS */}
        <TabsContent value="rotinas" className="mt-6 grid gap-6 lg:grid-cols-2">
          {/* Manhã */}
          <div className="rounded-3xl border border-border bg-card p-7 flex flex-col justify-between" data-testid="routine-morning">
            <div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5"><Sunrise className="h-6 w-6 text-gold" /><h3 className="font-serif text-2xl font-medium">Pela manhã</h3></div>
                <SourceTag source={{ pdfPage: 43, printedPage: "41" }} />
              </div>
              <p className="mt-2 text-sm text-muted-foreground">Comece o dia criando espaço para energia, clareza e presença.</p>
              <ol className="mt-5 space-y-4">
                {MORNING_ROUTINE.map((r, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold/15 font-serif font-semibold text-gold">{i + 1}</span>
                    <div><p className="font-medium text-foreground">{r.titulo}</p><p className="text-sm text-muted-foreground">{r.texto}</p></div>
                  </li>
                ))}
              </ol>

              {/* Lembretes e Check-in da Manhã */}
              <div className="mt-6 grid gap-3 sm:grid-cols-2 border-t border-border/60 pt-5">
                <div className="rounded-2xl border border-border/80 bg-secondary/30 p-3.5 text-xs">
                  <p className="font-semibold text-destructive/80 uppercase tracking-wider flex items-center gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5" /> Evite começar no automático
                  </p>
                  <p className="mt-1 text-muted-foreground leading-relaxed">{MORNING_EXTRAS.evite}</p>
                </div>
                <div className="rounded-2xl border border-border/80 bg-secondary/30 p-3.5 text-xs">
                  <p className="font-semibold text-gold uppercase tracking-wider flex items-center gap-1.5">
                    <Heart className="h-3.5 w-3.5" /> Lembre-se
                  </p>
                  <p className="mt-1 text-muted-foreground leading-relaxed">{MORNING_EXTRAS.lembrete}</p>
                </div>
              </div>

              <div className="mt-4 rounded-2xl border border-border/60 p-3 text-center">
                <span className="text-[11px] font-sans uppercase tracking-wider text-muted-foreground">Check-in rápido:</span>
                <div className="mt-1.5 flex flex-wrap justify-center gap-2">
                  {MORNING_EXTRAS.checkIn.map((c) => (
                    <span key={c} className="rounded-full bg-secondary px-2.5 py-0.5 text-xs text-foreground font-medium">{c}</span>
                  ))}
                </div>
              </div>
            </div>

            <p className="mt-5 text-center font-serif text-sm italic text-muted-foreground">
              “{MORNING_EXTRAS.frase}”
            </p>
          </div>

          {/* Noite */}
          <div className="rounded-3xl border border-border bg-card p-7 flex flex-col justify-between" data-testid="routine-evening">
            <div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5"><Moon className="h-6 w-6 text-gold" /><h3 className="font-serif text-2xl font-medium">À noite</h3></div>
                <SourceTag source={{ pdfPage: 44, printedPage: "42" }} />
              </div>
              <p className="mt-2 text-sm text-muted-foreground">Desacelere para que o corpo possa restaurar sua energia.</p>
              <ol className="mt-5 space-y-4">
                {EVENING_ROUTINE.map((r, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 font-serif font-semibold text-primary">{i + 1}</span>
                    <div><p className="font-medium text-foreground">{r.titulo}</p><p className="text-sm text-muted-foreground">{r.texto}</p></div>
                  </li>
                ))}
              </ol>

              {/* Lembretes e Check-in da Noite */}
              <div className="mt-6 grid gap-3 sm:grid-cols-2 border-t border-border/60 pt-5">
                <div className="rounded-2xl border border-border/80 bg-secondary/30 p-3.5 text-xs">
                  <p className="font-semibold text-destructive/80 uppercase tracking-wider flex items-center gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5" /> Evite levar o dia para a cama
                  </p>
                  <p className="mt-1 text-muted-foreground leading-relaxed">{EVENING_EXTRAS.evite}</p>
                </div>
                <div className="rounded-2xl border border-border/80 bg-secondary/30 p-3.5 text-xs">
                  <p className="font-semibold text-gold uppercase tracking-wider flex items-center gap-1.5">
                    <Heart className="h-3.5 w-3.5" /> Lembre-se
                  </p>
                  <p className="mt-1 text-muted-foreground leading-relaxed">{EVENING_EXTRAS.lembrete}</p>
                </div>
              </div>

              <div className="mt-4 rounded-2xl border border-border/60 p-3 text-center">
                <span className="text-[11px] font-sans uppercase tracking-wider text-muted-foreground">Check-in noturno:</span>
                <div className="mt-1.5 flex flex-wrap justify-center gap-2">
                  {EVENING_EXTRAS.checkIn.map((c) => (
                    <span key={c} className="rounded-full bg-secondary px-2.5 py-0.5 text-xs text-foreground font-medium">{c}</span>
                  ))}
                </div>
              </div>
            </div>

            <p className="mt-5 text-center font-serif text-sm italic text-muted-foreground">
              “{EVENING_EXTRAS.frase}”
            </p>
          </div>
        </TabsContent>

        {/* CHECKLIST */}
        <TabsContent value="checklist" className="mt-6 space-y-5">
          <div className="rounded-3xl border border-gold/25 bg-card p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-serif text-xl font-medium">Checklist diário de autocuidado</h3>
                <p className="text-sm text-muted-foreground">{new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" })}</p>
              </div>
              <div className="flex items-center gap-4">
                <SourceTag source={{ pdfPage: 45, printedPage: "43" }} />
                <button data-testid="checklist-reset" onClick={reset} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
                  <RotateCcw className="h-4 w-4" /> Recomeçar
                </button>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Progresso</span><span className="font-medium text-foreground">{done}/{allItems.length} · {pct}%</span></div>
              <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-secondary">
                <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <ChecklistColumn title="Manhã" items={CHECKLIST.manha} state={data.items || {}} onToggle={toggle} testid="checklist-manha" />
            <ChecklistColumn title="Durante o dia" items={CHECKLIST.dia} state={data.items || {}} onToggle={toggle} testid="checklist-dia" />
            <ChecklistColumn title="Noite" items={CHECKLIST.noite} state={data.items || {}} onToggle={toggle} testid="checklist-noite" />
          </div>

          <div className="rounded-3xl border border-border bg-card p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h4 className="text-xs uppercase tracking-widest text-gold">Escalas de percepção (1 a 5)</h4>
                <p className="text-xs text-muted-foreground mt-0.5">Registre com calma sua percepção corporal para hoje.</p>
              </div>
              <div className="rounded-2xl border border-gold/30 bg-gold/5 px-3 py-1.5 text-xs text-foreground max-w-xs">
                <p className="font-semibold text-gold uppercase text-[10px] tracking-wider">Não é cobrança</p>
                <p className="text-[11px] text-muted-foreground">Se algum item não aconteceu, apenas observe. Amanhã é uma nova oportunidade.</p>
              </div>
            </div>

            {/* Escalas numéricas de 1 a 5 apenas para Energia, Sono e Digestão */}
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              {CHECKLIST_ESCALAS.map((k) => (
                <div key={k} className="rounded-2xl border border-border/70 p-3.5 bg-secondary/20">
                  <p className="text-sm font-medium text-foreground">{k}</p>
                  <div className="mt-2 flex gap-1.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        data-testid={`escala-${k}-${n}`}
                        onClick={() => setEscala(k, n)}
                        className={`h-8 w-8 rounded-full text-sm font-medium transition-colors ${data.escalas?.[k] === n ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-gold/20"}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Campos textuais conforme o PDF: Emoção predominante e Sinal do corpo que mais apareceu */}
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {CHECKLIST_TEXTOS.map((k) => (
                <div key={k}>
                  <label className="block text-xs uppercase tracking-wider text-muted-foreground font-medium">{k}</label>
                  <input
                    type="text"
                    data-testid={`checklist-texto-${k.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                    value={data.textos?.[k] || (typeof data.escalas?.[k] === "string" ? data.escalas[k] : "")}
                    onChange={(e) => setTexto(k, e.target.value)}
                    placeholder="Descreva com palavras simples..."
                    className="mt-1.5 w-full rounded-xl border border-input bg-background p-2.5 text-sm outline-none focus:ring-2 focus:ring-gold"
                  />
                </div>
              ))}
            </div>

            {/* O que meu corpo tentou me dizer hoje? */}
            <div className="mt-6">
              <label className="block text-xs uppercase tracking-wider text-muted-foreground font-medium">O que meu corpo tentou me dizer hoje?</label>
              <textarea
                data-testid="checklist-nota"
                value={data.nota || ""}
                onChange={(e) => setData((d) => ({ ...d, nota: e.target.value }))}
                rows={2}
                placeholder="Anotação livre sobre o seu dia..."
                className="mt-1.5 w-full rounded-xl border border-input bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-gold"
              />
            </div>
          </div>

          <SafetyNote>{CHECKLIST_NOTE}</SafetyNote>
        </TabsContent>
      </Tabs>
    </div>
  );
}
